// Renders FAQ/article markdown content into React elements
// Supports: **bold**, *italic*, [text](url), bullet lists, numbered lists, headings

import type { ReactNode } from 'react';
import { Box, Typography, Link } from '@/ui';

interface MarkdownRendererProps {
  content: string;
}

// Standalone bold callouts render in pink by default (matching the source doc's highlight style).
// Lines listed here render in bold black instead — add the exact callout text (no leading/trailing
// asterisks, no trailing period) as reviewed by the content team.
const BLACK_CALLOUT_TEXTS = new Set<string>([
  'IE Hub has no influence over these decisions',
  'It does not apply for benefits on your behalf',
]);

function parseInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Pattern: **bold**, *italic*, [text](url)
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|\[(.+?)\]\((.+?)\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      // **bold**
      nodes.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      // *italic*
      nodes.push(<em key={key++}>{match[3]}</em>);
    } else if (match[4] && match[5]) {
      // [text](url)
      // The link text itself may contain bold markers
      const linkText = match[4].replace(/\*\*/g, '');
      nodes.push(
        <Link data-testid="features-support-faqs-components-parse-inline-link-action"
          key={key++}
          href={match[5]}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#004b49', textDecoration: 'underline' }}
        >
          {linkText}
        </Link>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : [text];
}

function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const lines = content.split('\n');
  const blocks: ReactNode[] = [];
  let blockKey = 0;
  let i = 0;

  while (i < lines.length) {
    const rawLine = lines[i];
    if (rawLine === undefined) { i++; continue; }
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) {
      i++;
      continue;
    }

    // Heading: # through ###### (h1-h6) with optional **bold** wrapping
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1]?.length ?? 3;
      const headingText = (headingMatch[2] ?? '').replace(/\*\*/g, '').trim();
      // Scale font size down as heading level grows deeper
      const fontSize = level <= 2 ? '18px' : level === 3 ? '16px' : '15px';
      const mt = level <= 2 ? 2.5 : level === 3 ? 2 : 1.5;
      blocks.push(
        <Typography data-testid="features-support-faqs-components-markdown-renderer-text-action-1"
          key={blockKey++}
          variant="h6"
          sx={{ fontWeight: 600, fontSize, color: '#343a40', mt, mb: 1 }}
        >
          {headingText}
        </Typography>
      );
      i++;
      continue;
    }

    // Unordered list: starts with * or -
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
      const listItems: ReactNode[] = [];
      while (i < lines.length) {
        const li = (lines[i] ?? '').trim();
        if (!li.startsWith('* ') && !li.startsWith('- ') && li !== '') {
          break;
        }
        if (li === '') {
          i++;
          continue;
        }
        const itemText = li.replace(/^\*\s+|- /, '').trim();
        listItems.push(
          <li key={blockKey++} style={{ marginBottom: '4px' }}>
            <Typography data-testid="features-support-faqs-components-markdown-renderer-text-action-2" variant="body1" component="span" sx={{ fontSize: '15px', color: '#343a40' }}>
              {parseInline(itemText)}
            </Typography>
          </li>
        );
        i++;
      }
      blocks.push(
        <Box data-testid="features-support-faqs-components-markdown-renderer-container-action-1" component="ul" key={blockKey++} sx={{ pl: 3, my: 1, listStyleType: 'disc' }}>
          {listItems}
        </Box>
      );
      continue;
    }

    // Ordered list: starts with number.
    if (/^\d+\.\s/.test(trimmed)) {
      const listItems: ReactNode[] = [];
      while (i < lines.length) {
        const rawLi = lines[i] ?? '';
        const liTrimmed = rawLi.trim();
        const isNumbered = /^\d+\.\s/.test(liTrimmed);
        const isIndented = rawLi.startsWith('   ') || rawLi.startsWith('\t');
        const isEmpty = liTrimmed === '';

        if (!isNumbered && !isEmpty && !isIndented) {
          break;
        }
        if (isEmpty) {
          i++;
          continue;
        }
        if (isNumbered) {
          const itemText = liTrimmed.replace(/^\d+\.\s+/, '').trim();
          listItems.push(
            <li key={blockKey++} style={{ marginBottom: '6px' }}>
              <Typography data-testid="features-support-faqs-components-markdown-renderer-text-action-3" variant="body1" component="span" sx={{ fontSize: '15px', color: '#343a40' }}>
                {parseInline(itemText)}
              </Typography>
            </li>
          );
        } else if (isIndented && liTrimmed) {
          // Indented continuation line (subtitle under a numbered item)
          listItems.push(
            <Typography data-testid="features-support-faqs-components-markdown-renderer-text-action-4"
              key={blockKey++}
              variant="body2"
              sx={{ fontSize: '14px', color: '#6c757d', pl: 2, mb: 0.5, mt: -0.5 }}
            >
              {parseInline(liTrimmed)}
            </Typography>
          );
        }
        i++;
      }
      blocks.push(
        <Box data-testid="features-support-faqs-components-markdown-renderer-container-action-2" component="ol" key={blockKey++} sx={{ pl: 3, my: 1 }}>
          {listItems}
        </Box>
      );
      continue;
    }

    // Standalone bold paragraph = highlighted callout
    // Default color: pink (#E91E7B), matching the Google Doc coral/red highlight.
    // Exceptions in BLACK_CALLOUT_TEXTS render in bold black instead (per content team review).
    // Pattern: entire line wrapped in **...** with no other text outside the bold markers
    if (/^\*\*[^*]+\*\*\.?\s*$/.test(trimmed)) {
      const calloutText = trimmed.replace(/^\*\*/, '').replace(/\*\*\.?\s*$/, '').trim();
      // Normalise for lookup: strip trailing period so override entries can omit it
      const calloutKey = calloutText.replace(/\.$/, '');
      const isBlack = BLACK_CALLOUT_TEXTS.has(calloutKey);
      blocks.push(
        <Typography data-testid="features-support-faqs-components-markdown-renderer-text-action-5"
          key={blockKey++}
          variant="body1"
          sx={{
            fontSize: '15px',
            color: isBlack ? '#111827' : '#E91E7B',
            fontWeight: isBlack ? 700 : 600,
            mb: 1.5,
            lineHeight: 1.6,
          }}
        >
          {parseInline(calloutText)}
        </Typography>
      );
      i++;
      continue;
    }

    // Regular paragraph
    blocks.push(
      <Typography data-testid="features-support-faqs-components-markdown-renderer-text-action-6"
        key={blockKey++}
        variant="body1"
        sx={{ fontSize: '15px', color: '#343a40', mb: 1.5, lineHeight: 1.6 }}
      >
        {parseInline(trimmed)}
      </Typography>
    );
    i++;
  }

  return <Box data-testid="features-support-faqs-components-markdown-renderer-container-action-3">{blocks}</Box>;
}

export default MarkdownRenderer;
